/* eslint-disable @typescript-eslint/no-explicit-any */

export enum FeedbackType {
    REBUKE = 'Rüge',
    CORRECTION = 'Korrektur',
    PRAISE = 'Lob/Feedback'
}

export interface BehavioralFeedback {
    id: string;
    clientId: string;
    type: FeedbackType;
    content: string;
    createdAt: string;
    acknowledgedAt: string | null;
}

export enum UserRole {
  DOMINA = 'DOMINA',
  SUB = 'SUB',
}

export enum Currency {
  CHF = 'CHF',
  EUR = '€',
  USD = '$',
  GBP = '£'
}

export enum TributeFrequency {
    DAILY = 'Täglich',
    WEEKLY = 'Wöchentlich',
    MONTHLY = 'Monatlich',
    YEARLY = 'Jährlich',
}

export enum TaskCategory {
  SUB = 'Sub Aufgabe',
  RITUAL = 'Ritual',
  PERSONAL = 'Eigener Termin',
}

export enum TaskType {
  SIMPLE = 'Einfach',
  DURATION = 'Dauer',
  COUNT = 'Anzahl',
  INTERVAL = 'Intervall',
}

export enum TaskStatus {
  PENDING = 'Offen',
  IN_PROGRESS = 'In Bearbeitung',
  COMPLETED = 'Erledigt',
  PENDING_REVIEW = 'Zur Kontrolle',
  OVERDUE = 'Überfällig',
  CLOSED_INCOMPLETE = 'Nicht erledigt geschlossen',
  FAILED = 'Gescheitert',
  REJECTED = 'Abgelehnt',
  DELETED = 'Gelöscht',
}

export enum RecurrenceType {
    DAILY = 'Täglich',
    WEEKLY = 'Wöchentlich',
    MONTHLY = 'Monatlich',
    YEARLY = 'Jährlich',
    CUSTOM = 'Benutzerdefiniert', 
}

export enum TaskVisibilityPreset {
    IMMEDIATE = 'Sofort sichtbar',
    H24_BEFORE = '24h vor Fälligkeit',
    D7_BEFORE = '7 Tage vor Fälligkeit',
    CUSTOM = 'Benutzerdefiniert (Datum & Uhrzeit)'
}

export enum PaymentMethod {
  BANK = 'Banküberweisung',
  CREDIT_CARD = 'Kreditkarte',
  PAYPAL = 'PayPal',
  TWINT = 'Twint',
}

export enum PaymentPurpose {
    TRIBUTE = 'Tribut',
    GIFT = 'Geschenk',
    FINE = 'Strafe',
    OTHER = 'Sonstiges',
}

export enum CalendarView {
    MONTH = 'Monat',
    WEEK = 'Woche',
    DAY = 'Tag',
}

export enum FeedbackTag {
    BRAV = 'BRAV',
    UNGEHORSAM = 'UNGEHORSAM',
    FRECH = 'FRECH',
    KEUSCH = 'KEUSCH',
    WICHSEN = 'WICHSEN',
    MELKEN = 'MELKEN',
    STRAFE = 'STRAFE',
    ANBETUNGSWÜRDIG = 'ANBETUNGSWÜRDIG',
    DIENSTFERTIG = 'DIENSTFERTIG',
    VERSPIELT = 'VERSPIELT',
    BEDÜRFTIG = 'BEDÜRFTIG',
    GELANGWEILT = 'GELANGWEILT',
    AUFMERKSAM = 'AUFMERKSAM',
    FAUL = 'FAUL',
    INSPIRIEREND = 'INSPIRIEREND',
    ENTTÄUSCHEND = 'ENTTÄUSCHEND',
    ERNIEDRIGT = 'ERNIEDRIGT',
    STOLZ = 'STOLZ',
    DANKBAR = 'DANKBAR',
    GIERIG = 'GIERIG',
    VERZWEIFELT = 'VERZWEIFELT',
    EIFRIG = 'EIFRIG',
    ZÖGERLICH = 'ZÖGERLICH',
    KREATIV = 'KREATIV',
    MÜDE = 'MÜDE',
    UNKONZENTRIERT = 'UNKONZENTRIERT',
    VORBILDLICH = 'VORBILDLICH',
    NACHLÄSSIG = 'NACHLÄSSIG',
    DETAILREICH = 'DETAILREICH',
    OBERFLÄCHLICH = 'OBERFLÄCHLICH',
    EHRLICH = 'EHRLICH',
    UNEHRLICH = 'UNEHRLICH',
    DEMÜTIG = 'DEMÜTIG',
    AUFMÜPFIG = 'AUFMÜPFIG',
    SELBSTREFLEKTIERT = 'SELBSTREFLEKTIERT',
    JAMMERND = 'JAMMERND',
}

export enum UserTheme {
  AMETHYST = 'amethyst',
  AMETHYST_LIGHT = 'amethyst-light',
  EMERALD = 'emerald',
  EMERALD_LIGHT = 'emerald-light',
  ROSE = 'rose',
  ROSE_LIGHT = 'rose-light',
  CELESTIAL = 'celestial',
  CELESTIAL_LIGHT = 'celestial-light',
  PURE_WHITE = 'pure-white',
  MONO_GRAY = 'mono-gray',
  MONO_GRAY_LIGHT = 'mono-gray-light'
}

export enum NavigationLayout {
  SIDEBAR = 'sidebar',           // Klassische Seitenleiste (Links)
  TOP_TABS = 'top_tabs',         // Horizontale Registerkarten (Oben)
  BOTTOM_TABS = 'bottom_tabs',   // App-Style Registerkarten (Unten)
  FLOATING_PILL = 'floating_pill'// Schwebendes Tab-Menü (Gleitend)
}

export interface User {
  id: string;
  email: string;
  role: UserRole;
  clientId?: string;
  theme?: UserTheme;
  navigationLayout?: NavigationLayout;
  telegramChatId?: string;
  timezone?: string;
}

export type ContentItemType = 'TEXT' | 'IMAGE' | 'LINK' | 'PDF';

export interface ContentItem {
    id: string;
    type: ContentItemType;
    content: string;
    title?: string;
}

export interface WeeklyPlanItem {
    id: string;
    day: number; // 0-6 (Mon-Sun)
    startTime: string;
    endTime?: string;
    duration?: number;
    activity: string;
    isObligatory: boolean;
    checklist?: ChecklistItem[];
}

export interface WorkTime {
    day: number;
    start: string;
    end: string;
}

export interface DailyPlan {
    id: string;
    clientId: string;
    date: string; // YYYY-MM-DD
    items: WeeklyPlanItem[];
    checklist?: ChecklistItem[];
    workStart?: string;
    workEnd?: string;
    wakeUpTime?: string;
    bedTime?: string;
    isFreeDay?: boolean;
    isSickDay?: boolean;
    isSealed: boolean;
    sealedAt?: string;
}

export interface Client {
  id: string;
  name: string;
  email: string;
  entryDate: string;
  birthDate: string;
  periodEndDate?: string; // YYYY-MM-DD - Enddatum der aktuellen Begleitungs-Periode
  timezone?: string; // e.g. "Europe/Berlin", "Europe/Zurich"
  isActive: boolean;
  isPaused: boolean;
  preferences: string;
  taboos: string;
  theme?: UserTheme;
  lastLogin?: string;
  contact: {
    telegram: string;
    phone: string;
    address: {
      street: string;
      zip: string;
      city: string;
      country: string;
    };
  };
  financials: {
    monthlyTribute: number;
    tributeFrequency: TributeFrequency;
    currency: Currency;
    includedServices: string;
  };
  profile: {
    about: string;
    timezone?: string;
    periodEndDate?: string;
    income?: number;
    assets?: number;
    weeklyPlan?: WeeklyPlanItem[];
    weeklyWorkTimes?: WorkTime[];
    freeDayIndices?: number[];
    wakeUpTimes?: Record<number, string>; // 0-6 -> "06:00"
    bedTimes?: Record<number, string>; // 0-6 -> "22:00"
    weekData?: Record<string, {
        plan: WeeklyPlanItem[];
        workTimes: WorkTime[];
        freeDayIndices: number[];
    }>;
    sealedWeeks?: Record<string, string>; // Key: "2024-KW12" -> Sealed Date ISO
    dayChecklists?: Record<number, ChecklistItem[]>;
    rulebookIds?: string[];
    reportTemplateIds?: string[];
  };
  goddessNotes: string;
  rulebookId: string | null;
  rulebookIds?: string[];
  reportTemplateId: string | null;
  reportTemplateIds?: string[];
  telegramChatId?: string;
  contract: {
      lastUpdated: string;
      items: ContentItem[];
  };
  messages: {
      lastUpdated: string;
      items: ContentItem[];
  };
}

export interface RulebookLabel {
    name: string;
    color: string;
}

export interface Rulebook {
    id: string;
    title: string;
    content: string;
    label: RulebookLabel;
}

export interface TaskLabel {
    id: string;
    userId: string;
    name: string;
    color: string;
}

export interface TaskTemplate {
    id: string;
    title: string;
    description: string;
    category: TaskCategory;
    type: TaskType;
    durationTarget?: number;
    countTarget?: number;
    intervalConfig?: any[];
    startDate?: string;
    endDate?: string; 
    deadlineTime?: string;
    recurrence?: RecurrenceType;
    recurrenceEndType?: 'DATE' | 'INDEFINITE';
    recurrenceEndDate?: string | null;
    visibilityPreset: TaskVisibilityPreset;
    customVisibilityDate?: string;
    reviewRequired: boolean;
    isReportRequired?: boolean;
    isProofRequired?: boolean;
    attachmentLink?: string;
    labelId?: string | null;
    isMultiDay?: boolean;
    goddessNotes?: string;
    goddessReminders?: any[];
    label?: TaskLabel | null;
    assignedClientIds?: string[];
}

export interface Comment {
    id: string;
    author: 'DOMINA' | 'SUB';
    text: string;
    createdAt: string;
    tags?: FeedbackTag[];
}

export interface ProofItem {
    id: string;
    name: string;
    url: string;
    type: 'IMAGE' | 'VIDEO' | 'FILE' | 'TEXT';
    submittedAt: string;
}

export interface TaskInstance {
    id: string;
    clientId: string;
    templateId: string | null;
    seriesId?: string | null;
    title: string;
    description: string;
    category: TaskCategory;
    type: TaskType;
    status: TaskStatus;
    startDateTime: string;
    endDateTime: string;
    visibilityDate: string;
    completedAt?: string;
    reviewRequired: boolean;
    isReportRequired: boolean;
    isProofRequired: boolean;
    durationTarget?: number;
    durationProgress?: number;
    countTarget?: number;
    countProgress?: number;
    intervalConfig?: any[];
    labelId: string | null;
    isMultiDay?: boolean;
    comments: Comment[];
    proofs?: ProofItem[];
    reportContent?: string;
    attachmentLink?: string;
    goddessNotes?: string;
    goddessReminders?: any[];
    isBlocking?: boolean;
    clientName?: string;
    label?: TaskLabel | null;
    createdAt: string;
    updatedAt: string;
    // Coaching integration
    coachingTopicId?: string;
    coachingTopicTitle?: string;
    coachingGoalId?: string;
    coachingGoalTitle?: string;
    coachingReflectionPrompt?: string;
    coachingReflectionAnswer?: string;
    coachingCatFeedback?: string;
    // To-Do integration
    isTodo?: boolean;
    todoScope?: TodoScope;
}

export type TodoScope = 'DAY' | 'WEEK' | 'MONTH';

export interface ClientTodoItem {
  id: string;
  clientId: string;
  title: string;
  description?: string;
  scope: TodoScope;
  dueDateTime: string; // ISO String
  isCompleted: boolean;
  completedAt?: string;
  createdAt: string;
  taskId?: string;
}

export type CalendarEventItem = TaskInstance;

export interface Payment {
    id: string;
    clientId: string;
    clientName?: string;
    amount: number;
    currency: Currency;
    method: PaymentMethod;
    date: string;
    purpose: PaymentPurpose;
    description?: string;
    tributeStartDate?: string;
    tributeEndDate?: string;
}

export interface PaymentWithClient extends Payment {
    clientName: string;
}

export interface DiaryEntry {
    id: string;
    clientId: string;
    clientName?: string;
    author: 'SUB' | 'GODDESS';
    date: string;
    endDate?: string; 
    type: 'GENERAL' | 'CONFESSION' | 'SICK_DAY' | 'DAY_OFF_REQUEST';
    content: string;
    goddessFeedback?: {
        tags: FeedbackTag[];
        notes: string;
        isApproved?: boolean; 
    };
    // Coaching integration
    coachingTopicId?: string;
    coachingTopicTitle?: string;
    coachingGoalId?: string;
    coachingGoalTitle?: string;
}

export interface DailyReport {
    date: string; // YYYY-MM-DD
    clientId: string;
    diaryEntries: DiaryEntry[];
    messages: Message[];
    payments: Payment[];
    tasks: TaskInstance[];
}

export interface DominaDashboardStats {
  activeClients: number;
  overdueTasks: number;
  monthlyTribute: { [key in Currency]?: number };
  upcomingDeadlines: TaskInstance[];
  recentActivities: { id: string; text: string; createdAt: string; link: string }[];
  recentReports: ReportSubmission[];
  recentDiaryEntries: DiaryEntry[];
}

export enum FlashAlertStatus {
    PENDING = 'PENDING',
    COMPLETED = 'COMPLETED',
    LATE = 'LATE',
}

export enum FlashAlertTriggerType {
    FIXED = 'FIXED', 
    RANDOM = 'RANDOM',
}

export enum FlashAlertRandomPeriod {
    DAY = 'DAY',
    WEEK = 'WEEK',
    MONTH = 'MONTH',
}

export interface FlashAlertConfig {
    id: string;
    clientId: string;
    title: string;
    description?: string;
    requiresPhoto: boolean;
    deadlineMinutes?: number;
    triggerType: FlashAlertTriggerType;
    fixedTimes?: string[]; // e.g. ["08:00", "22:00"]
    randomFrequency?: number;
    randomPeriod?: FlashAlertRandomPeriod;
    randomStartTime?: string; // e.g. "08:00"
    randomEndTime?: string;   // e.g. "22:00"
    startDate: string;
    endDate?: string;
    isActive: boolean;
    createdAt: string;
}

export interface FlashAlertInstance {
    id: string;
    configId: string | null;
    config?: FlashAlertConfig;
    clientId: string;
    status: FlashAlertStatus;
    triggeredAt: string;
    deadlineAt?: string;
    completedAt?: string;
    photoUrl?: string;
    clientComment?: string;
    dominaComment?: string;
    isNotified?: boolean;
    createdAt: string;
}

export interface SubDashboardData {
    todaysTasks: TaskInstance[];
    tomorrowTasks?: TaskInstance[];
    latestFeedback: DiaryEntry | null;
    rulebook: Rulebook | null;
    latestMessage: Message | null;
    payments: Payment[];
    client: Client | null;
}

export interface Folder {
    id: string;
    name: string;
    assignedClientIds: string[] | null;
}
export interface FileItem {
    id: string;
    name: string;
    url: string;
    type: 'IMAGE' | 'VIDEO' | 'PDF' | 'LINK' | 'FILE';
    folderId: string | null;
    assignedClientIds: string[] | null;
    createdAt: string;
}

export interface ChecklistItem {
    id: string;
    text: string;
    checked: boolean;
}
export interface DailyChecklist {
    id: string;
    clientId: string;
    date: string;
    items: ChecklistItem[];
}
export interface DefaultChecklistItem {
    id: string;
    text: string;
    sortOrder: number;
    assignedClientIds: string[] | null;
}

export enum DominaStatus {
    ONLINE = "Verfügbar",
    BUSY = "Beschäftigt",
    DO_NOT_DISTURB = "Nicht stören",
    PLAYFUL = "Spielerisch",
    AWAKE = "Wach",
    GOING_TO_BED = "Gehe zu Bett",
    OFFLINE = "Offline"
}
export interface DominaStatusRecord {
    id: number;
    status: DominaStatus;
    comment?: string;
    updatedAt: string;
}

export enum ReportQuestionType {
  SHORT_TEXT = 'Kurze Antwort',
  PARAGRAPH = 'Absatz',
  MULTIPLE_CHOICE = 'Multiple Choice',
  CHECKBOXES = 'Kontrollkästchen',
  CHECKBOX_GRID = 'Kontrollkästchen-Gitter',
  LINEAR_SCALE = 'Lineare Skala',
  NUMBER = 'Zahl',
  TIME = 'Zeit',
  FILE_UPLOAD = 'Dateiupload',
  TIME_LOG = 'Zeiterfassung',
}

export interface ReportQuestionOption {
  id: string;
  value: string;
}

export interface ReportGridRow {
    id: string;
    label: string;
}
export interface ReportGridColumn {
    id: string;
    label: string;
}

export interface ReportQuestion {
  id: string;
  text: string;
  type: ReportQuestionType;
  isRequired: boolean;
  options?: ReportQuestionOption[];
  gridRows?: ReportGridRow[];
  gridColumns?: ReportGridColumn[];
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
}

export interface ReportSection {
  id: string;
  title: string;
  description?: string;
  questions: ReportQuestion[];
}

export interface ReportTemplate {
  id: string;
  title: string;
  description?: string;
  sections: ReportSection[];
}

export interface TimeLogEntry {
  id: string;
  startTime: string;
  endTime: string;
  activity: string;
}

export type ReportAnswerValue = 
  | string 
  | string[] 
  | number 
  | { [rowId: string]: string }
  | { name: string; url: string }
  | TimeLogEntry[];

export interface ReportAnswer {
  questionId: string;
  value: ReportAnswerValue;
}

export interface ReportSubmissionFeedback {
  notes: string;
  rating?: number; // 1-10
  tags?: string[];
  isApproved?: boolean;
  createdAt?: string;
}

export interface ReportSubmission {
  id: string;
  templateId: string;
  clientId: string;
  submittedAt: string;
  answers: ReportAnswer[];
  clientName?: string;
  templateTitle?: string;
  feedback?: ReportSubmissionFeedback;
}

export interface AppNotification {
  id: string;
  user_id: string;
  content: string;
  link: string;
  is_read: boolean;
  created_at: string;
}

export enum ControlStatus {
    GREEN = 'GREEN',
    YELLOW = 'YELLOW',
    RED = 'RED'
}

export interface ControlGoal {
    id: string;
    configId: string;
    area: string;
    title: string;
    description: string;
    isRequired: boolean;
    frequency: 'WEEKLY' | 'MONTHLY';
    createdAt: string;
}

export interface ControlConfig {
    id: string;
    clientId: string;
    startDate: string;
    durationWeeks: number;
    status: ControlStatus;
    complianceScore: number;
    activeAreas: string[];
    isActive: boolean;
    summary?: string;
    createdAt: string;
    updatedAt: string;
}

export interface WeeklyControlCheck {
    id: string;
    configId: string;
    weekNumber: number;
    submittedAt: string;
    areaChecks: Record<string, { 
        completed: boolean; 
        comment?: string; 
        measures?: string 
    }>;
    revenue?: number;
    energyLevel: number;
    eveningEnergyLevel: number;
    isCompleted: boolean;
    notes?: string;
    dominaNotes?: string;
}

export interface EmotionDefinition {
    id: string;
    label: string;
    emoji: string;
    isDefault: boolean;
    sortOrder: number;
}

export interface EmotionLog {
    id: string;
    clientId: string;
    emotionId: string;
    timestamp: string;
    emotionLabel?: string;
    emotionEmoji?: string;
}

export interface EmotionStats {
    emotionId: string;
    label: string;
    emoji: string;
    count: number;
    lastLogged?: string;
}

export interface QuotedMessageInfo {
  id: string;
  content: string;
  sender: UserRole;
}

export interface MessageReaction {
  emoji: string;
  userIds: string[];
}

export type MessageType = 'TEXT' | 'IMAGE' | 'VIDEO' | 'FILE' | 'AUDIO';

export interface Message {
  id: string;
  localId?: string;
  conversationId: string;
  sender: UserRole;
  content: string;
  timestamp: string;
  isRead: boolean;
  isEdited?: boolean;
  type: MessageType;
  reactions: MessageReaction[];
  replyTo: QuotedMessageInfo | null;
  audioUrl?: string | null;
  audioDuration?: number;
  status?: 'sending' | 'sent' | 'failed';
}

export interface MessageStats {
    today: { domina: number; sub: number };
    week: { domina: number; sub: number };
    month: { domina: number; sub: number };
}

export interface Conversation {
  id: string;
  clientId: string;
  clientName: string;
  clientIsPaused?: boolean;
  dominaId: string;
  lastMessage: Message | null;
  lastMessageTimestamp: string | null;
  unreadCount: number;
  isArchived?: boolean;
}

export type SendMessagePayload = {
    conversationId: string;
    sender: UserRole;
    content: string;
    type: MessageType;
    replyTo?: QuotedMessageInfo;
    audioUrl?: string;
    audioDuration?: number;
};

export enum AudioPeriodType {
    DATE_RANGE = 'DATE_RANGE',
    CALENDAR_WEEK = 'CALENDAR_WEEK',
    CUSTOM = 'CUSTOM',
}

export enum AudioIntervalType {
    EVERY_DAY = 'EVERY_DAY',
    EVERY_SECOND_DAY = 'EVERY_SECOND_DAY',
}

export interface AudioInputItem {
    id: string;
    title: string;
    url: string;
    assignedClientIds?: string[] | null;
    unlockTime?: string | null;
    periodType?: AudioPeriodType | null;
    startDate?: string | null;
    endDate?: string | null;
    calendarWeeks?: string[] | null;
    intervalType?: AudioIntervalType | null;
}

export interface DailyAudioInput {
    id: string;
    title: string;
    audios: AudioInputItem[];
    assignedClientIds: string[] | null;
    periodType: AudioPeriodType;
    startDate: string | null;
    endDate: string | null;
    calendarWeeks: string[] | null;
    intervalType: AudioIntervalType;
    intervalDays: number;
    createdAt: string;
    isTemplate?: boolean;
}

// ==========================================
// COACHING MODULE TYPES & INTERFACES
// ==========================================

export type CoachingTabId = 
  | 'overview' 
  | 'goals' 
  | 'plan' 
  | 'month_planner'
  | 'topics' 
  | 'tasks' 
  | 'reflection' 
  | 'sessions' 
  | 'progress';

export interface CoachingDayPlan {
  date: string; // YYYY-MM-DD
  focusTheme?: string;
  notes?: string;
  milestoneTitle?: string;
}

export interface CoachingMonthPlan {
  monthKey: string; // e.g. "2026-03"
  focusTheme?: string;
  leitmotif?: string;
  notes?: string;
  dayPlans?: Record<string, CoachingDayPlan>;
}

export enum CoachingGoalCategory {
  PERSONAL = 'Persönlich',
  RELATIONSHIPS = 'Beziehungen',
  CAREER = 'Beruf',
  HEALTH = 'Gesundheit',
  EVERYDAY = 'Alltag',
  SELF_LEADERSHIP = 'Selbstführung',
  INDIVIDUAL = 'Individuell',
}

export enum CoachingGoalPriority {
  LOW = 'Niedrig',
  MEDIUM = 'Mittel',
  HIGH = 'Hoch',
  URGENT = 'Dringend',
}

export enum CoachingGoalStatus {
  ACTIVE = 'Aktiv',
  ACHIEVED = 'Erreicht',
  PAUSED = 'Pausiert',
  DISCARDED = 'Verworfen',
}

export interface CoachingGoal {
  id: string;
  clientId: string;
  parentGoalId?: string | null; // For sub-goals
  title: string;
  description: string;
  category: CoachingGoalCategory;
  startDate: string;
  targetDate: string;
  priority: CoachingGoalPriority;
  status: CoachingGoalStatus;
  progress: number; // 0 - 100
  personalNotes?: string;
  successCriteria: string[]; // Konkrete Erfolgskriterien
  initialSituation?: string; // Ausgangslage des Klienten vor dem Coaching
  isMainGoal?: boolean;
  evaluatedByReflectionId?: string; // ID der Reflexion, durch die Cat den Fortschritt eingeschätzt hat
  evaluatedByReflectionTitle?: string; // Titel der Bewertungs-Reflexion
  evaluatedAt?: string; // Datum der letzten Einschätzung durch Cat
  createdAt: string;
  updatedAt: string;
}

export interface CoachingPhaseActionArea {
  area: string; // e.g. "Alkohol", "Struktur", "Beruf"
  items: string[];
}

export interface CoachingPhase {
  id: string;
  order: number;
  name: string; // e.g. "Phase 1: Sicherheit und Ordnung"
  description?: string;
  durationWeeks?: number;
  startDate?: string;
  endDate?: string;
  topicIds?: string[];
  isActive?: boolean;
  isCompleted?: boolean;
  successCriterion?: string; // Das oberste Erfolgskriterium dieser Phase
  actionAreas?: CoachingPhaseActionArea[]; // Spezifische Handlungsfelder mit Aufgaben/Maßnahmen
}

export interface CoachingDailyRoutine {
  morning?: string[]; // Morgens (z.B. 5 Min: Drei Prioritäten)
  daytime?: string[]; // Tagsüber (z.B. 5-15 Min: Ordnungsaufgabe / Spaziergang)
  evening?: string[]; // Abends (z.B. 5 Min: 3 Reflexionsfragen)
}

export interface CoachingFrustrationProtocol {
  questions: string[]; // z.B. die 4 Fragen
  oldPattern: string;  // altes destruktives Muster
  newPattern: string;  // neues konstruktives Handlungsmuster
  notes?: string;
}

export interface CoachingPlan {
  id: string;
  clientId: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  mainGoalId?: string;
  currentPhaseId?: string;
  currentPhaseName?: string;
  currentWeekText?: string; // e.g. "Woche 4 von 12"
  currentFocusText?: string; // e.g. "Morgenroutine festigen"
  catImpulseMessage?: string; // Persönlicher Impuls von Cat
  catImpulseDate?: string;
  phases: CoachingPhase[];
  assignedTopicIds: string[];
  enabledTabs?: CoachingTabId[]; // Tab visibility configured by Cat
  monthPlans?: Record<string, CoachingMonthPlan>; // Monthly planning keyed by YYYY-MM
  coachGuidelines?: string[]; // Leitsätze & Haltung für die Führung als Coach
  dailyRoutine?: CoachingDailyRoutine; // Tägliches Pflichtprogramm (z.B. 15-30 Min)
  frustrationProtocol?: CoachingFrustrationProtocol; // 4-Fragen-Modell bei Frustration / Selbstmitleid
  createdAt: string;
  updatedAt: string;
}

export type CoachingTopicCategory =
  | 'Selbstführung'
  | 'Selbstwahrnehmung'
  | 'Grenzen'
  | 'Beziehungen'
  | 'Emotionale Themen'
  | 'Lebensgestaltung'
  | 'Personalisierte Flüsterraum Themen'
  | 'Individuell';

export interface CoachingTopic {
  id: string;
  clientId?: string | null; // null if global blueprint template
  isTemplate?: boolean;
  title: string;
  category: CoachingTopicCategory | string;
  description: string;
  coachingGoal?: string;
  reflectionQuestions: string[];
  exercises: string[];
  suggestedTasks?: string[];
  audioUrl?: string;
  whisperRoomPrompt?: string;
  followUpQuestions?: string[];
  closingReflection?: string;
  internalCatNotes?: string; // STRICTLY CAT ONLY - NEVER SENT TO CLIENT
  isArchived?: boolean;
  status?: 'Geplant' | 'Aktiv' | 'Vertieft' | 'Abgeschlossen';
  assignedDate?: string;
  createdAt: string;
  updatedAt: string;
}

export type CoachingReflectionQuestionType =
  | 'FREITEXT'
  | 'KURZANTWORT'
  | 'JA_NEIN'
  | 'SKALA_1_10'
  | 'AUSWAHL'
  | 'MEHRFACHAUSWAHL'
  | 'CHECKBOX';

export interface CoachingReflectionQuestion {
  id: string;
  prompt: string;
  type: CoachingReflectionQuestionType;
  options?: string[]; // for selection types
  required?: boolean;
  helpText?: string;
}

export interface CoachingReflectionAnswer {
  questionId: string;
  questionPrompt: string;
  answer: string | number | boolean | string[];
}

export interface CoachingReflection {
  id: string;
  clientId: string;
  topicId?: string;
  topicTitle?: string;
  goalId?: string;
  goalTitle?: string;
  title: string;
  description?: string;
  questions: CoachingReflectionQuestion[];
  answers?: CoachingReflectionAnswer[];
  status: 'OFFEN' | 'BEANTWORTET' | 'KOMMENTIERT';
  dueDate?: string;
  submittedAt?: string;
  catFeedback?: string; // Personal feedback from Cat
  catFeedbackDate?: string;
  catGoalProgressAssessment?: Record<string, number>; // Ziel-Fortschrittseinschätzung durch Cat (goalId -> 0-100%)
  createdAt: string;
}

export interface CoachingSession {
  id: string;
  clientId: string;
  date: string; // ISO date/time
  durationMinutes: number; // e.g. 60
  topic: string;
  preparationNotes?: string;
  internalCatNotes?: string; // STRICTLY FOR CAT - NEVER SHOWN TO CLIENT
  clientVisibleInsights?: string; // Erkenntnisse für Klient
  clientVisibleAgreements?: string; // Vereinbarungen
  tasksAgreed?: string[]; // Aufgaben aus der Session
  nextStep?: string;
  nextAppointmentDate?: string;
  isClientVisible: boolean; // Cat can toggle visibility
  status: 'GEPLANT' | 'DURCHGEFÜHRT' | 'VERSCHOBEN' | 'STORNIERT';
  createdAt: string;
  updatedAt: string;
}

export interface CoachingClientSummary {
  clientId: string;
  clientName: string;
  mainGoal?: string;
  currentPhase?: string;
  currentTopic?: string;
  currentFocus?: string;
  currentWeek?: string;
  openTasksCount: number;
  overdueTasksCount: number;
  lastReflectionDate?: string;
  lastSessionDate?: string;
  nextSessionDate?: string;
  catImpulseMessage?: string;
  overallProgress: number; // 0 - 100
}

export interface CoachingApplication {
  id: string;
  clientId: string;
  clientName?: string;
  challenges: string;
  goalsExpectations?: string;
  commitmentConfirmed: boolean;
  status: 'OFFEN' | 'ANGENOMMEN' | 'ABGELEHNT';
  submittedAt: string;
  reviewedAt?: string;
  catNotes?: string;
}


